'use client';

import { useCallback, useEffect, useRef, useState } from 'react';
import Link from 'next/link';
import { motion } from 'framer-motion';
import {
  BarChart3,
  Calendar,
  Check,
  ChevronLeft,
  ChevronRight,
  ClipboardCheck,
  MessageSquare,
  Send,
} from 'lucide-react';
import BookDemoSection from '@/components/BookDemoSection';
import InlineStyle from '@/components/InlineStyle';
import { BookDemoButton, Button, Card, FaqAccordion, PageSection, SectionTitle } from '@/components/ui';
import { MetricsBand } from '@/components/primitives/MetricsBand';
import { fadeUp, springSoft, staggerContainer, viewportOnce } from '@/lib/motion';

/* metadata in ./layout.js — this page is a client component for the tabs + carousel. */

/* ── Graphics (as supplied — do not crop or recolour) ───────────────────── */
const IMG = '/images/publishing';

/* ── Content ────────────────────────────────────────────────────────────── */
const METRICS = [
  { value: '7', label: 'Channels covered' },
  { value: '1', label: 'Calendar for every brand' },
  { value: '17', label: 'Post formats supported' },
  { value: '100%', label: 'Posts through approval' },
];

const VALUE_CARDS = [
  {
    Icon: Calendar,
    img: 'one-calendar-all-channels.png',
    alt: 'Content calendar surrounded by Facebook, Instagram, LinkedIn, TikTok, X, YouTube and Google Business icons',
    title: 'One calendar, scheduled ahead',
    text: 'Every brand and every channel in a single view: drafted, in review, scheduled, live. Set the date and time per profile and let the queue ship it, so peak-hour posts and multi-market rollouts go out without anyone watching the clock.',
  },
  {
    Icon: Send,
    img: 'publish-everywhere.png',
    alt: 'A single post composed once and published to Facebook and X profiles',
    title: 'Publish everywhere without remaking the post',
    text: 'Reels, Stories, Shorts, carousels and documents, built once in one composer and sent to every profile in a single action, with the tags and hashtags already on them.',
  },
  {
    Icon: BarChart3,
    img: 'post-performance.png',
    alt: 'Impressions and engagement analytics for published posts',
    title: 'Track performance and improve',
    text: 'Impressions, engagement and reach at channel level and on every individual post, reported where you planned it. Next month gets built on what actually performed.',
  },
  {
    Icon: ClipboardCheck,
    img: 'review-approve-publish.png',
    alt: 'A post approved by a reviewer with comments from teammates',
    title: 'Review, approve, publish',
    text: 'Maker and checker built in. A reviewer comments, requests changes and approves on the post itself, so everything that goes live has been seen by the person accountable for it.',
  },
  {
    Icon: MessageSquare,
    img: 'comments-next-to-listening.png',
    alt: 'Comment thread beside a published post',
    title: 'Publishing that sits next to listening',
    text: 'The comments your post generates land in the same platform as your response team. Campaign and response stop living in two systems with two owners.',
  },
];

/* ── Capability mock-ups (CSS-built product UI) ─────────────────────────── */
const Skel = ({ w, h = 6, soft, style }) => (
  <span className={`pub-skel${soft ? ' pub-skel--soft' : ''}`} style={{ width: w, height: h, ...style }} />
);

const Chip = ({ tone = 'brand', children }) => (
  <span className={`pub-chip pub-chip--${tone}`}>
    <i />
    {children}
  </span>
);

/* 5 rows x 7 days. 0 = empty, 1 = brand, 2 = brand-dark, 3 = tint, 4 = neutral. */
const CAL_CELLS = [0, 1, 0, 0, 3, 0, 0, 0, 2, 0, 0, 3, 0, 1, 0, 0, 4, 0, 0, 3, 0, 0, 1, 0, 0, 4, 0, 0];

function CalendarMock() {
  return (
    <div className="pub-mock pub-mock--card">
      <div className="pub-mock__bar">
        <Skel w={64} />
        <span style={{ display: 'flex', gap: 4 }}>
          <Chip>Live</Chip>
          <Chip tone="muted">Draft</Chip>
        </span>
      </div>
      <div className="pub-cal pub-cal--days">
        {['Su', 'Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa'].map((d) => (
          <span key={d}>{d}</span>
        ))}
      </div>
      <div className="pub-cal">
        {CAL_CELLS.map((c, i) => (
          <span key={i} className={`pub-cal__cell pub-cal__cell--${c}`} />
        ))}
      </div>
    </div>
  );
}

function ComposerMock() {
  return (
    <div className="pub-mock-stack">
      <div className="pub-mock pub-mock--card" style={{ display: 'flex', flexDirection: 'column', gap: 7 }}>
        <div style={{ display: 'flex', gap: 5 }}>
          <Chip>Instagram</Chip>
          <Chip>LinkedIn</Chip>
          <Chip tone="muted">X</Chip>
        </div>
        <Skel w="88%" />
        <Skel w="72%" soft />
        <div style={{ display: 'flex', gap: 6, marginTop: 2 }}>
          <span className="pub-thumb pub-thumb--a" />
          <span className="pub-thumb pub-thumb--b" />
          <span className="pub-btn-skel" />
        </div>
      </div>
      <div style={{ display: 'flex', gap: 8 }}>
        {['Reel', 'Carousel', 'Story'].map((f) => (
          <div key={f} className="pub-mock pub-mock--card pub-format">
            <span>{f}</span>
            <span className="pub-format__box" />
          </div>
        ))}
      </div>
    </div>
  );
}

function ReviewMock() {
  return (
    <div className="pub-mock-stack" style={{ gap: 9 }}>
      <div className="pub-mock pub-mock--card pub-review">
        <span className="pub-avatar" />
        <div className="pub-review__body">
          <Skel w="70%" />
          <Skel w="46%" soft />
          <span className="pub-flag">Changes requested</span>
        </div>
      </div>
      <div className="pub-mock pub-mock--card pub-review" style={{ marginLeft: 18 }}>
        <span className="pub-avatar pub-avatar--dark" />
        <div className="pub-review__body">
          <Skel w="58%" />
          <Skel w="38%" soft />
        </div>
      </div>
      <div className="pub-mock pub-mock--card pub-review" style={{ alignItems: 'center' }}>
        <span className="pub-tick">
          <Check size={14} strokeWidth={3} />
        </span>
        <span className="pub-review__done">Approved by brand lead</span>
      </div>
    </div>
  );
}

function QueueMock() {
  const rows = [
    ['09:00', 'Instagram', 'var(--brand)'],
    ['11:30', 'LinkedIn', 'var(--brand-dark)'],
    ['14:00', 'X', 'var(--neutral-gray)'],
    ['18:45', 'YouTube', 'var(--brand-border)'],
  ];
  return (
    <div className="pub-mock pub-mock--card" style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
      <div className="pub-mock__bar" style={{ marginBottom: 0 }}>
        <Skel w={70} />
        <Chip>Queued</Chip>
      </div>
      {rows.map(([time, name, dot]) => (
        <div key={time} className="pub-queue">
          <b>{time}</b>
          <i style={{ background: dot }} />
          <span>{name}</span>
          <Skel w={34} h={8} />
        </div>
      ))}
    </div>
  );
}

function MetricsMock() {
  const bars = [
    ['34%', 'var(--border-med)'],
    ['52%', 'var(--brand-border)'],
    ['44%', 'var(--border-med)'],
    ['70%', 'var(--brand-border)'],
    ['58%', 'var(--border-med)'],
    ['86%', 'var(--brand)'],
    ['96%', 'var(--brand-dark)'],
  ];
  return (
    <div className="pub-mock-stack" style={{ gap: 10 }}>
      <div style={{ display: 'flex', gap: 8 }}>
        <div className="pub-mock pub-mock--card pub-kpi">
          <span>Impressions</span>
          <b>3,300</b>
        </div>
        <div className="pub-mock pub-mock--card pub-kpi">
          <span>Engagement</span>
          <b style={{ color: 'var(--brand)' }}>6.4%</b>
        </div>
      </div>
      <div className="pub-mock pub-mock--card pub-bars">
        {bars.map(([h, bg], i) => (
          <span key={i} style={{ height: h, background: bg }} />
        ))}
      </div>
    </div>
  );
}

const CAPABILITIES = [
  {
    id: 'calendar',
    tab: 'Content calendar',
    Mock: CalendarMock,
    eyebrow: 'Plan',
    title: 'A content calendar that shows every brand and every channel',
    text: 'One view showing what is drafted, in review, scheduled and live, filterable by brand, channel, campaign or owner.',
    points: [
      ['Week and month views', 'across all connected profiles'],
      ['Status at a glance', 'for draft, in review, scheduled and published'],
      ['Filter by brand, channel or campaign', 'in multi-brand accounts'],
      ['Gap spotting', 'so no channel goes quiet by accident'],
    ],
  },
  {
    id: 'builder',
    tab: 'Post builder',
    Mock: ComposerMock,
    eyebrow: 'Create',
    title: 'Write the post once, shape it for every network',
    text: 'Copy, creative, hashtags, tags and links in one window, shaped to whatever format each network expects.',
    points: [
      ['Multi-profile publishing', 'to every connected account at once'],
      ['Native format support', 'for Reels, Stories, Shorts, carousels and documents'],
      ['Hashtags, mentions and links', 'attached before it goes out'],
      ['Per-profile preview', 'so you see the post the way the audience will'],
    ],
  },
  {
    id: 'approvals',
    tab: 'Approvals and collaboration',
    Mock: ReviewMock,
    eyebrow: 'Review',
    title: 'Maker and checker, built into the flow',
    text: 'Nothing reaches a live audience without the person accountable for it seeing it first.',
    points: [
      ['Draft, review, approve', 'as a single tracked workflow'],
      ['Inline comments and change requests', 'on the post itself'],
    ],
  },
  {
    id: 'scheduling',
    tab: 'Scheduling and publishing',
    Mock: QueueMock,
    eyebrow: 'Ship',
    title: 'Scheduled to the minute, per profile',
    text: 'Set the exact date and time each profile should post, or publish immediately.',
    points: [
      ['Per-profile scheduling', 'down to the minute'],
      ['Publish now or queue ahead', 'for entire campaigns'],
      ['Time zone and market staggering', 'from one composer'],
      ['Edit or reschedule', 'right up to publish time'],
    ],
  },
  {
    id: 'performance',
    tab: 'Post-level performance',
    Mock: MetricsMock,
    eyebrow: 'Measure',
    title: 'The numbers come back to where you planned',
    text: 'The numbers land on the same calendar item that scheduled the post, not in a month-end export.',
    points: [
      ['Post-level metrics', 'attached to each calendar item'],
      ['Channel-level performance', 'across your whole social presence'],
      ['Format and timing comparison', 'to find what earns attention'],
      ['Owned and earned in one platform', 'alongside listening data'],
    ],
  },
];

const CHANNELS = [
  { name: 'Instagram', color: '#E1306C', formats: ['Reel', 'Story', 'Carousel', 'Image post'] },
  { name: 'Facebook', color: '#1877F2', formats: ['Static post', 'Reel', 'Story'] },
  { name: 'LinkedIn', color: '#0A66C2', formats: ['Static post', 'Carousel', 'Document', 'Video'] },
  { name: 'X', color: '#E3EAF2', formats: ['Static post', 'Video'] },
  { name: 'YouTube', color: '#FF4D4D', formats: ['Shorts', 'Video'] },
  { name: 'TikTok', color: '#2BE8E0', formats: ['Video'] },
  { name: 'Google My Business', color: '#34A853', formats: ['Photo'] },
];

const AUDIENCES = [
  {
    title: 'Social media and brand teams',
    text: 'Run the calendar for every brand you own, keep the schedule visible to everyone, and prove what the content earned without stitching exports together.',
  },
  {
    title: 'Agencies and multi-brand groups',
    text: 'Separate calendars per client or brand, with every post reviewed before it reaches their audience.',
  },
  {
    title: 'Regulated and enterprise marketing',
    text: 'Maker and checker on every post, and one calendar showing everything scheduled across every brand, so nothing goes live without someone seeing it first.',
  },
];

const PLATFORM = [
  {
    title: 'Publishing',
    text: 'Plan, approve, schedule and publish across every social channel from one calendar.',
  },
  {
    title: 'Social Listening',
    text: 'Track brand mentions, campaign reaction and share of voice across social, news and review sites.',
    href: '/platform/social-intelligence',
  },
  {
    title: 'Response Management',
    text: 'Reply to the comments and messages your posts generate from a unified inbox with full customer context.',
    href: '/platform/holistic-digital-care',
  },
  {
    title: 'ContextualPulse™',
    text: 'Read sentiment, emotion and intent behind every reaction so you know why a post landed the way it did.',
    href: '/ai-stack/contextualpulse',
  },
];

const FAQS = [
  {
    q: 'What is Locobuzz Publishing?',
    a: 'Locobuzz Publishing is the social media publishing module of the Locobuzz customer experience platform. It lets you plan a content calendar, create a post once in a shared composer, route it through an approval workflow, schedule and publish it to every connected social profile, and then measure how each individual post performed, all in the same workspace where you already listen to and respond to your audience.',
  },
  {
    q: 'Which social media channels can I publish to?',
    a: 'You can publish to Instagram, Facebook, LinkedIn, X, YouTube, TikTok and Google My Business, also known as GMB or Google Business Profile. Each channel supports its own native formats, including Instagram Reels, Stories, carousels and image posts, Facebook static posts, Reels and Stories, LinkedIn static posts, carousels, documents and video, X static posts and video, YouTube Shorts and video, TikTok video, and Google My Business photos. You can connect multiple profiles per channel and multiple brands per account.',
  },
  {
    q: 'Can I publish the same post to multiple social accounts at once?',
    a: 'Yes. The composer is built for multi-profile publishing. You write the copy, add the creative, hashtags, tags and links once, select every profile it should go to, and send it in a single action. You do not rebuild the post per network, and you can preview how it will appear on each profile before it goes out.',
  },
  {
    q: 'Does Locobuzz Publishing include an approval workflow?',
    a: 'Yes. A maker and checker workflow is built in. A creator drafts the post, and a reviewer opens the same post to comment, request changes or approve it. Nothing reaches a live audience without the person accountable for it seeing it first.',
  },
  {
    q: 'What performance metrics does Publishing report?',
    a: 'Publishing reports impressions, engagement rate, reach and interactions at channel level and on every individual post. The metrics appear on the same calendar item that scheduled the post, so you can compare formats, timings and channels without exporting data to a spreadsheet at the end of the month.',
  },
  {
    q: 'How is this different from a standalone social media scheduling tool?',
    a: 'A standalone scheduler publishes and stops there. Locobuzz Publishing sits inside a platform that also handles social listening and response management, so the comments and mentions your post generates land in the same workspace, with the same customer context, for the same team. Owned content performance and earned conversation are reported together rather than in two disconnected systems.',
  },
];

/* ── Value carousel ─────────────────────────────────────────────────────── */
function ValueRail() {
  const railRef = useRef(null);
  const [edge, setEdge] = useState({ start: true, end: false });

  const sync = useCallback(() => {
    const r = railRef.current;
    if (!r) return;
    setEdge({ start: r.scrollLeft <= 4, end: r.scrollLeft >= r.scrollWidth - r.clientWidth - 4 });
  }, []);

  useEffect(() => {
    sync();
    window.addEventListener('resize', sync);
    return () => window.removeEventListener('resize', sync);
  }, [sync]);

  const nudge = (dir) => {
    const r = railRef.current;
    if (!r) return;
    r.scrollBy({ left: dir * r.clientWidth, behavior: 'smooth' });
  };

  return (
    <>
      <div className="pub-rail" ref={railRef} onScroll={sync}>
        {VALUE_CARDS.map(({ Icon, img, alt, title, text }) => (
          <article key={title} className="pub-vcard">
            <div className="pub-vcard__img">
              <img src={`${IMG}/${img}`} alt={alt} width={559} height={315} loading="lazy" decoding="async" />
            </div>
            <span className="pub-icon" aria-hidden="true">
              <Icon size={22} color="var(--brand)" strokeWidth={2} />
            </span>
            <h3 className="pub-vcard__title">{title}</h3>
            <p className="pub-p pub-vcard__text">{text}</p>
          </article>
        ))}
      </div>
      <div className="pub-rail-ctl">
        <button type="button" aria-label="Previous" onClick={() => nudge(-1)} disabled={edge.start}>
          <ChevronLeft size={18} strokeWidth={2.2} />
        </button>
        <button type="button" aria-label="Next" onClick={() => nudge(1)} disabled={edge.end}>
          <ChevronRight size={18} strokeWidth={2.2} />
        </button>
      </div>
    </>
  );
}

/* ── Capability tabs ────────────────────────────────────────────────────── */
function CapabilityTabs() {
  const [active, setActive] = useState(0);
  const tabRefs = useRef([]);

  const onKeyDown = (e) => {
    const last = CAPABILITIES.length - 1;
    let next = null;
    if (e.key === 'ArrowRight') next = active === last ? 0 : active + 1;
    else if (e.key === 'ArrowLeft') next = active === 0 ? last : active - 1;
    else if (e.key === 'Home') next = 0;
    else if (e.key === 'End') next = last;
    if (next === null) return;
    e.preventDefault();
    setActive(next);
    tabRefs.current[next]?.focus();
  };

  return (
    <>
      <div role="tablist" aria-label="Publishing capabilities" className="pub-tabs" onKeyDown={onKeyDown}>
        {CAPABILITIES.map((c, i) => (
          <button
            key={c.id}
            ref={(el) => (tabRefs.current[i] = el)}
            role="tab"
            type="button"
            id={`pub-tab-${c.id}`}
            aria-selected={active === i}
            aria-controls={`pub-panel-${c.id}`}
            tabIndex={active === i ? 0 : -1}
            className={`pub-tab${active === i ? ' is-active' : ''}`}
            onClick={() => setActive(i)}
          >
            {c.tab}
          </button>
        ))}
      </div>

      {/* All panels share one grid cell so the section height never jumps between tabs. */}
      <div className="pub-panels">
        {CAPABILITIES.map((c, i) => (
          <div
            key={c.id}
            role="tabpanel"
            id={`pub-panel-${c.id}`}
            aria-labelledby={`pub-tab-${c.id}`}
            aria-hidden={active !== i}
            className={`pub-panel${active === i ? ' is-active' : ''}`}
          >
            <div className="pub-panel__visual">
              <c.Mock />
            </div>
            <div className="pub-panel__copy">
              <span className="pub-panel__eyebrow">{c.eyebrow}</span>
              <h3>{c.title}</h3>
              <p className="pub-p">{c.text}</p>
              <ul className="pub-points">
                {c.points.map(([lead, rest]) => (
                  <li key={lead}>
                    <span className="pub-points__tick" aria-hidden="true">
                      <Check size={12} color="var(--brand)" strokeWidth={3} />
                    </span>
                    <span>
                      <b>{lead}</b> {rest}
                    </span>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        ))}
      </div>
    </>
  );
}

/* ── Page ───────────────────────────────────────────────────────────────── */
export default function PublishingPage() {
  return (
    <div className="pub-root">
      {/* 1 — Hero */}
      <section className="pub-hero">
        <div aria-hidden="true" className="pub-hero__wash" />
        <div className="container pub-hero__grid">
          <motion.div variants={staggerContainer(0.1)} initial="hidden" animate="show" className="pub-hero__copy">
            <motion.span variants={fadeUp} className="lb-eyebrow">
              Locobuzz Publishing
            </motion.span>
            <motion.h1 variants={fadeUp}>
              Plan, publish and report your entire social media strategy from one{' '}
              <span className="serif-accent italic">calendar</span>.
            </motion.h1>
            <motion.p variants={fadeUp} className="pub-p pub-hero__lede">
              <strong>What is Publishing?</strong> Locobuzz Publishing is a single tool that lets a team plan, create,
              review, schedule and publish content across every social network from one place, then measure how each post
              performed.
            </motion.p>
            <motion.div variants={fadeUp} className="pub-hero__actions">
              <BookDemoButton label="Book a Demo" />
              <Button href="#capabilities" variant="secondary" size="lg">
                See capabilities
              </Button>
            </motion.div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 18 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ ...springSoft, delay: 0.15 }}
            className="pub-hero__visual"
          >
            <img
              src={`${IMG}/hero-content-calendar.png`}
              alt="Content calendar with scheduled posts across Facebook, Instagram, TikTok, X and YouTube"
              width={775}
              height={730}
              fetchPriority="high"
              decoding="async"
            />
          </motion.div>
        </div>
      </section>

      {/* 2 — Proof metrics */}
      <MetricsBand items={METRICS} columns={4} />

      {/* 3 — The value */}
      <PageSection id="value" bg="tint">
        <SectionTitle
          eyebrow="The value"
          title={
            <>
              Plan, collaborate, publish, <span className="serif-accent italic">improve</span>.
            </>
          }
          subtitle="One calendar, one composer, one place the numbers come back to, instead of a native app per channel and a spreadsheet at the end of the month."
        />
        <motion.div variants={fadeUp} initial="hidden" whileInView="show" viewport={viewportOnce}>
          <ValueRail />
        </motion.div>
      </PageSection>

      {/* 4 — Capabilities */}
      <PageSection id="capabilities" bg="page">
        <SectionTitle
          eyebrow="Capabilities"
          title={
            <>
              Everything social media publishing software should do, in{' '}
              <span className="serif-accent italic">one workspace</span>
            </>
          }
          subtitle="Five capabilities that take a post from an idea on a calendar to a number you can act on."
        />
        <CapabilityTabs />
      </PageSection>

      {/* 5 — Channel coverage (dark) */}
      <PageSection id="channels" bg="dark">
        <SectionTitle
          eyebrow={<span style={{ color: 'var(--brand-sky)' }}>Channel coverage</span>}
          title={
            <span style={{ color: 'var(--text-inverse)' }}>
              One publisher. Every network <span className="serif-accent italic">your audience is on</span>.
            </span>
          }
          subtitle={<span style={{ color: 'var(--glass-text-body)' }}>Connect your profiles once and publish native content to each of them from the same window.</span>}
        />
        <div className="pub-hub">
          <div className="pub-hub__top">
            <i aria-hidden="true" />
            <b>Universal publisher</b>
            <span>7 channels</span>
          </div>
          <p className="pub-p">One post, built once, sent to every connected profile in its own native format.</p>
        </div>
        <div className="pub-hub__line" aria-hidden="true" />
        <motion.div
          className="pub-channels"
          variants={staggerContainer(0.06)}
          initial="hidden"
          whileInView="show"
          viewport={viewportOnce}
        >
          {CHANNELS.map((c) => (
            <motion.div key={c.name} variants={fadeUp} className="pub-chan" style={{ borderTopColor: c.color }}>
              <h3>{c.name}</h3>
              <em>
                {c.formats.length} {c.formats.length === 1 ? 'format' : 'formats'}
              </em>
              <ul>
                {c.formats.map((f) => (
                  <li key={f}>{f}</li>
                ))}
              </ul>
            </motion.div>
          ))}
        </motion.div>
      </PageSection>

      {/* 6 — Who it is for */}
      <PageSection bg="page">
        <SectionTitle
          eyebrow="Who it is for"
          title={
            <>
              Built for teams that <span className="serif-accent italic">publish at scale</span>
            </>
          }
        />
        <motion.div
          className="pub-grid pub-grid--3"
          variants={staggerContainer(0.08)}
          initial="hidden"
          whileInView="show"
          viewport={viewportOnce}
        >
          {AUDIENCES.map((a) => (
            <motion.div key={a.title} variants={fadeUp}>
              <Card padding="xl" radius="2xl" hover className="pub-card">
                <h3 className="pub-card__title">{a.title}</h3>
                <p className="pub-p pub-card__text">{a.text}</p>
              </Card>
            </motion.div>
          ))}
        </motion.div>
      </PageSection>

      {/* 7 — One platform */}
      <PageSection bg="tint">
        <SectionTitle
          eyebrow="One platform"
          title={
            <>
              Publishing is stronger because it <span className="serif-accent italic">does not stand alone</span>
            </>
          }
          subtitle="Most tools publish or listen. Locobuzz does both in the same workspace, so the post you schedule and the conversation it starts are handled by one platform and one set of numbers."
          maxSub="lg"
        />
        <motion.div
          className="pub-grid pub-grid--4"
          variants={staggerContainer(0.08)}
          initial="hidden"
          whileInView="show"
          viewport={viewportOnce}
        >
          {PLATFORM.map((p) => {
            const body = (
              <Card padding="lg" radius="xl" hover={Boolean(p.href)} className="pub-card pub-card--sm">
                <h3 className="pub-card__title">{p.title}</h3>
                <p className="pub-p pub-card__text">{p.text}</p>
              </Card>
            );
            return (
              <motion.div key={p.title} variants={fadeUp}>
                {p.href ? (
                  <Link href={p.href} className="pub-card-link">
                    {body}
                  </Link>
                ) : (
                  body
                )}
              </motion.div>
            );
          })}
        </motion.div>
      </PageSection>

      {/* 8 — FAQ */}
      <PageSection id="faqs" bg="page">
        <SectionTitle
          eyebrow="FAQ"
          title={
            <>
              Frequently asked <span className="serif-accent italic">questions</span>
            </>
          }
        />
        <FaqAccordion items={FAQS} columns={2} name="publishing-faq" />
      </PageSection>

      {/* 9 — Book a demo (always last) */}
      <BookDemoSection />

      <InlineStyle
        css={`
          /* All rules are scoped to .pub-root. Paragraph colours use two classes to beat the global "main p" theme rule. */
          .pub-root { --pub-gap: 12px; }
          .pub-root .pub-p { color: var(--text-body); margin: 0; }

          /* Hero */
          .pub-hero { position: relative; overflow: hidden; background: var(--gradient-page); padding: var(--hero-pad); }
          .pub-hero__wash { position: absolute; inset: 0; pointer-events: none; background: var(--gradient-brand-hero); }
          .pub-hero__grid { position: relative; z-index: 1; display: grid; grid-template-columns: minmax(0, 1.05fr) minmax(0, 0.95fr); gap: clamp(32px, 4vw, 56px); align-items: center; }
          .pub-hero__copy h1 { font-size: clamp(34px, 4.3vw, 52px); line-height: 1.06; font-weight: 800; letter-spacing: -0.02em; color: var(--brand-deep); margin: 0 0 20px; text-wrap: pretty; }
          .pub-root .pub-hero__lede { font-size: 17px; line-height: 1.7; max-width: 54ch; margin: 0 0 28px; }
          .pub-root .pub-hero__lede strong { color: var(--text-heading); font-weight: 700; }
          .pub-hero__actions { display: flex; flex-wrap: wrap; gap: 14px; }
          .pub-hero__visual { display: flex; justify-content: center; }
          .pub-hero__visual img { width: 100%; max-width: 560px; height: auto; }

          /* Value carousel */
          .pub-rail { display: flex; gap: 20px; overflow-x: auto; scroll-snap-type: x mandatory; scrollbar-width: none; padding: 4px 2px 8px; }
          .pub-rail::-webkit-scrollbar { display: none; }
          .pub-vcard { flex: 0 0 calc((100% - 40px) / 3); min-width: 270px; scroll-snap-align: start; display: flex; flex-direction: column; background: var(--surface-card); border: 1px solid var(--border-light); border-radius: var(--radius-xl); box-shadow: var(--shadow-card); padding: 24px; transition: transform .2s ease, box-shadow .2s ease; }
          .pub-vcard:hover { transform: translateY(-3px); box-shadow: var(--shadow-card-hover); }
          /* Backdrop matches the baked-in background of the supplied graphics so contain-fit leaves no seam. */
          .pub-vcard__img { aspect-ratio: 16 / 9; border-radius: var(--radius-lg); overflow: hidden; background: #E4E9F2; margin-bottom: 20px; }
          .pub-vcard__img img { width: 100%; height: 100%; object-fit: contain; }
          .pub-icon { display: inline-flex; align-items: center; justify-content: center; flex: none; width: 44px; height: 44px; border-radius: var(--radius-lg); background: var(--brand-subtle); border: 1px solid var(--brand-border); margin-bottom: 14px; }
          .pub-vcard__title { font-size: 19px; line-height: 1.3; font-weight: 700; letter-spacing: -0.01em; color: var(--text-heading); margin: 0 0 8px; min-height: 2.6em; }
          .pub-root .pub-vcard__text { font-size: 14.5px; line-height: 1.65; }
          .pub-rail-ctl { display: flex; justify-content: center; gap: 10px; margin-top: 22px; }
          .pub-rail-ctl button { width: 42px; height: 42px; border-radius: 50%; display: inline-flex; align-items: center; justify-content: center; background: var(--surface-card); border: 1px solid var(--border-med); color: var(--brand); transition: border-color .2s, background .2s, opacity .2s; }
          .pub-rail-ctl button:hover:not(:disabled) { border-color: var(--brand); background: var(--brand-subtle); }
          .pub-rail-ctl button:disabled { opacity: .4; cursor: default; }

          /* Capability tabs */
          .pub-tabs { display: flex; justify-content: space-between; gap: 10px 32px; border-bottom: 1px solid var(--border-med); margin-bottom: 32px; overflow-x: auto; scrollbar-width: none; }
          .pub-tabs::-webkit-scrollbar { display: none; }
          .pub-tab { flex: none; background: none; border: 0; border-bottom: 2px solid transparent; margin-bottom: -1px; padding: 0 2px 14px; font-size: 16px; font-weight: 700; letter-spacing: -0.01em; color: var(--text-heading); white-space: nowrap; transition: color .18s ease, border-color .18s ease; }
          .pub-tab:hover { color: var(--brand); }
          .pub-tab.is-active { color: var(--brand); border-bottom-color: var(--brand); }
          .pub-panels { display: grid; background: var(--surface-card); border: 1px solid var(--border-light); border-radius: var(--radius-2xl); box-shadow: var(--shadow-card); }
          .pub-panel { grid-area: 1 / 1; display: grid; grid-template-columns: minmax(0, 1fr) minmax(0, 1fr); gap: clamp(24px, 3vw, 44px); align-items: center; padding: clamp(22px, 3vw, 36px); visibility: hidden; opacity: 0; pointer-events: none; }
          .pub-panel.is-active { visibility: visible; opacity: 1; pointer-events: auto; animation: pubRise .35s ease both; }
          @keyframes pubRise { from { opacity: 0; transform: translateY(8px); } to { opacity: 1; transform: none; } }
          .pub-panel__visual { align-self: stretch; display: flex; align-items: center; justify-content: center; background: var(--surface-page); border-radius: var(--radius-xl); padding: clamp(18px, 2.2vw, 30px); }
          .pub-panel__visual > * { width: 100%; }
          .pub-panel__eyebrow { display: block; font-size: 12px; font-weight: 800; letter-spacing: .14em; text-transform: uppercase; color: var(--brand); margin-bottom: 12px; }
          .pub-panel__copy h3 { font-size: clamp(21px, 2.1vw, 26px); line-height: 1.25; font-weight: 700; letter-spacing: -0.02em; color: var(--text-heading); margin: 0 0 12px; text-wrap: pretty; }
          .pub-root .pub-panel__copy .pub-p { font-size: 16px; line-height: 1.65; }
          .pub-points { list-style: none; padding: 0; margin: 24px 0 0; display: grid; grid-template-columns: repeat(2, minmax(0, 1fr)); gap: 14px 24px; }
          .pub-points li { display: flex; align-items: flex-start; gap: 12px; font-size: 14.5px; line-height: 1.6; color: var(--text-body); }
          .pub-points b { color: var(--text-heading); font-weight: 700; }
          .pub-points__tick { flex: none; display: inline-flex; align-items: center; justify-content: center; width: 22px; height: 22px; margin-top: 1px; border-radius: 50%; background: var(--brand-subtle); border: 1.5px solid var(--brand-border); }

          /* Mock-ups */
          .pub-mock-stack { display: flex; flex-direction: column; gap: 10px; }
          .pub-mock--card { background: var(--surface-card); border: 1px solid var(--border-light); border-radius: var(--radius-lg); box-shadow: var(--shadow-card); padding: 12px; }
          .pub-mock__bar { display: flex; align-items: center; justify-content: space-between; margin-bottom: 10px; }
          .pub-skel { display: block; border-radius: 4px; background: rgba(13, 27, 62, .10); }
          .pub-skel--soft { background: rgba(13, 27, 62, .06); }
          .pub-chip { display: inline-flex; align-items: center; gap: 5px; font-size: 9.5px; font-weight: 700; line-height: 1; white-space: nowrap; border-radius: 5px; padding: 5px 6px; }
          .pub-chip i { width: 6px; height: 6px; border-radius: 2px; background: currentColor; }
          .pub-chip--brand { color: var(--brand-dark); background: var(--brand-subtle); }
          .pub-chip--muted { color: var(--text-muted); background: var(--surface-inset); }
          .pub-cal { display: grid; grid-template-columns: repeat(7, 1fr); grid-auto-rows: 30px; gap: 4px; }
          .pub-cal--days { grid-auto-rows: auto; margin-bottom: 5px; }
          .pub-cal--days span { font-size: 9px; font-weight: 700; text-align: center; color: var(--neutral-gray); }
          .pub-cal__cell { border-radius: 6px; background: var(--surface-page); border: 1px solid var(--border-light); }
          .pub-cal__cell--1 { background: var(--brand); border-color: var(--brand); }
          .pub-cal__cell--2 { background: var(--brand-dark); border-color: var(--brand-dark); }
          .pub-cal__cell--3 { background: var(--brand-border); border-color: transparent; }
          .pub-cal__cell--4 { background: rgba(13, 27, 62, .10); border-color: transparent; }
          .pub-thumb { width: 44px; height: 34px; border-radius: 7px; }
          .pub-thumb--a { background: var(--brand-border); }
          .pub-thumb--b { background: var(--brand-subtle); }
          .pub-btn-skel { margin-left: auto; align-self: flex-end; width: 56px; height: 24px; border-radius: 7px; background: var(--brand); }
          .pub-format { flex: 1; padding: 8px; display: flex; flex-direction: column; gap: 5px; }
          .pub-format > span:first-child { font-size: 9px; font-weight: 700; color: var(--neutral-gray); }
          .pub-format__box { height: 38px; border-radius: 6px; background: var(--surface-inset); }
          .pub-review { display: flex; align-items: flex-start; gap: 9px; }
          .pub-review__body { flex: 1; display: flex; flex-direction: column; gap: 6px; }
          .pub-avatar { flex: none; width: 26px; height: 26px; border-radius: 50%; background: rgba(13, 27, 62, .10); }
          .pub-avatar--dark { background: var(--brand-deep); }
          .pub-flag { align-self: flex-start; font-size: 9.5px; font-weight: 700; color: var(--warning-dark); background: var(--warning-bg); border-radius: 5px; padding: 3px 6px; }
          .pub-tick { flex: none; display: inline-flex; align-items: center; justify-content: center; width: 24px; height: 24px; border-radius: 50%; background: var(--success-bg); color: var(--success-dark); }
          .pub-review__done { font-size: 11px; font-weight: 700; color: var(--text-heading); }
          .pub-queue { display: flex; align-items: center; gap: 10px; background: var(--surface-page); border: 1px solid var(--border-light); border-radius: 9px; padding: 9px 10px; }
          .pub-queue b { width: 38px; font-size: 10.5px; font-weight: 800; color: var(--text-heading); }
          .pub-queue i { width: 8px; height: 8px; border-radius: 50%; }
          .pub-queue span:nth-of-type(1) { flex: 1; font-size: 10.5px; font-weight: 600; color: var(--text-body); }
          .pub-kpi { flex: 1; padding: 11px; }
          .pub-kpi span { display: block; font-size: 9.5px; font-weight: 700; color: var(--neutral-gray); margin-bottom: 5px; }
          .pub-kpi b { font-size: 17px; font-weight: 800; letter-spacing: -0.02em; color: var(--text-heading); }
          .pub-bars { display: flex; align-items: flex-end; gap: 7px; height: 118px; }
          .pub-bars span { flex: 1; border-radius: 5px 5px 0 0; }

          /* Channels (dark) */
          .pub-hub { max-width: 460px; margin: 0 auto; background: rgba(255, 255, 255, .06); border: 1px solid rgba(255, 255, 255, .14); border-radius: var(--radius-lg); padding: 18px 22px; }
          .pub-hub__top { display: flex; align-items: center; gap: 9px; margin-bottom: 8px; }
          .pub-hub__top i { width: 7px; height: 7px; border-radius: 50%; background: var(--brand); }
          .pub-hub__top b { font-size: 14px; font-weight: 700; color: var(--text-inverse); }
          .pub-hub__top span { margin-left: auto; font-size: 11px; font-weight: 700; padding: 3px 10px; border-radius: 999px; background: var(--brand-ring); color: var(--brand-sky); }
          .pub-root .pub-hub .pub-p { font-size: 13.5px; line-height: 1.55; color: var(--glass-text-muted); }
          .pub-hub__line { width: 2px; height: 28px; margin: 0 auto 4px; background: rgba(255, 255, 255, .18); }
          .pub-channels { display: flex; flex-wrap: wrap; justify-content: center; gap: var(--pub-gap); }
          .pub-chan { flex: 0 0 calc((100% - 3 * var(--pub-gap)) / 4); background: rgba(255, 255, 255, .05); border: 1px solid rgba(255, 255, 255, .12); border-top: 3px solid; border-radius: var(--radius-lg); padding: 16px 14px; }
          .pub-chan h3 { font-size: 14.5px; line-height: 1.25; font-weight: 700; color: var(--text-inverse); margin: 0 0 4px; }
          .pub-chan em { display: block; font-style: normal; font-size: 11px; font-weight: 600; color: var(--glass-text-muted); margin-bottom: 12px; }
          .pub-chan ul { list-style: none; padding: 0; margin: 0; display: flex; flex-direction: column; gap: 5px; }
          .pub-chan li { font-size: 12px; font-weight: 500; color: var(--glass-text-body); background: rgba(255, 255, 255, .06); border: 1px solid rgba(255, 255, 255, .10); border-radius: 6px; padding: 4px 9px; }

          /* Card grids */
          .pub-grid { display: grid; gap: 20px; }
          .pub-grid--3 { grid-template-columns: repeat(3, minmax(0, 1fr)); }
          .pub-grid--4 { grid-template-columns: repeat(4, minmax(0, 1fr)); gap: 16px; }
          .pub-grid > div { display: flex; }
          .pub-grid > div > * { flex: 1; }
          .pub-card { height: 100%; }
          .pub-card__title { font-size: 19px; line-height: 1.3; font-weight: 700; letter-spacing: -0.01em; color: var(--text-heading); margin: 0 0 10px; }
          .pub-root .pub-card__text { font-size: 15px; line-height: 1.65; }
          .pub-card--sm .pub-card__title { font-size: 16.5px; margin-bottom: 8px; }
          .pub-root .pub-card--sm .pub-card__text { font-size: 14px; }
          .pub-card-link { display: flex; height: 100%; color: inherit; }
          .pub-card-link > * { flex: 1; }

          /* ≥1100: seven channels on one row; keep the one two-line title ("Google My Business") from knocking the rows out of line */
          @media (min-width: 1100px) {
            .pub-chan { flex-basis: calc((100% - 6 * var(--pub-gap)) / 7); }
            .pub-chan h3 { min-height: 2.5em; }
          }
          @media (max-width: 1000px) {
            .pub-grid--4 { grid-template-columns: repeat(2, minmax(0, 1fr)); }
          }
          @media (max-width: 900px) {
            .pub-hero__grid { grid-template-columns: minmax(0, 1fr); }
            .pub-hero__visual { order: 2; }
            .pub-panel { grid-template-columns: minmax(0, 1fr); }
            .pub-grid--3 { grid-template-columns: minmax(0, 1fr); }
            .pub-tabs { justify-content: space-between; gap: 10px 18px; }
            .pub-tab { font-size: 14.5px; }
          }
          @media (max-width: 640px) {
            .pub-vcard { flex-basis: 86%; min-width: 0; }
            .pub-vcard__title { min-height: 0; }
            .pub-points { grid-template-columns: minmax(0, 1fr); }
            .pub-chan { flex-basis: calc((100% - var(--pub-gap)) / 2); }
            .pub-chan h3 { min-height: 2.5em; }
            .pub-grid--4 { grid-template-columns: minmax(0, 1fr); }
          }
        `}
      />
    </div>
  );
}
